import React, { useContext, useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import { Box, CircularProgress } from '@material-ui/core';
import { capitalizeAll, capitalizeFirst, getRobotDefinitionByRobotClass, getRobotTimeSeriesCollectionId, GenericMessageBox, useQueryParams }
    from '@moderepo/mode-app-components';
import clsx from 'clsx';
import { RCNumericMetricValueName } from '@moderepo/mode-apis';
import { ritzDataContext, selectProjectById } from '@moderepo/mode-data-state-ritz';
import { fetchRobotById, fetchRobotCatalog, rcDataContext, selectRobotById, selectRobotCatalog } from '@moderepo/mode-data-state-robot-cloud';
import { useRitzTranslation } from '../../utils/customHooks';
import { CollectionMetricsDataProvider, CollectionMetricsDataProviderProps }
    from '../time-series/CollectionMetricsDataProvider';
import { ChartSize } from '../time-series/tsUtils';



const chartViewOptions: CollectionMetricsDataProviderProps['viewOptions'] = {
    combineSeries: true,
    chartSize    : ChartSize.MEDIUM,
};

/**
 * excludedMetricsNames is the list of metric that we don't want to show as charts. Because different robot class has different set of metric names,
 * we can't know ahead of time what metric names to exclude. Therefore, this contains only a small list of value names that we know about that
 * should not be displayed as charts
 */
const dataViewTypeSettings: CollectionMetricsDataProviderProps['viewTypeSettings'] = {
    chart: {
        excludedMetricsNames: [RCNumericMetricValueName.LATITUDE, RCNumericMetricValueName.LONGITUDE, RCNumericMetricValueName.ALTITUDE],
    },
};



/**
 * This page Display ALL the Project's issues or Health status
 */
export const RCRobotHistoricalDataPage = () => {

    // Get the params from the route or url
    const params = useParams();
    const queryParams = useQueryParams();

    const projectId = Number(params.projectId);

    // The homeId can be embed in the URL params or queryParams
    const homeId = params.homeId || queryParams.get('homeId') ? Number(params.homeId || queryParams.get('homeId')) : undefined;
    const { robotId } = params;

    const { state:dataState } = useContext(ritzDataContext);
    const { state:rcDataState, dispatch:rcDataDispatch } = useContext(rcDataContext);
    const project = selectProjectById(dataState, projectId);
    const robot = selectRobotById(rcDataState, homeId, robotId);
    const robotDefinition = useMemo(() => {
        const robotCatalog = selectRobotCatalog(rcDataState, projectId);
        if (robot && robotCatalog) {
            return getRobotDefinitionByRobotClass(robotCatalog, robot.robotClass);
        }
        return undefined;
    }, [projectId, rcDataState, robot]);

    const robotCollectionId = useMemo(() => {
        if (robot) {
            return getRobotTimeSeriesCollectionId(robot.robotClass, robot.id);
        }
        return undefined;
    }, [robot]);


    const { trans } = useRitzTranslation(project);


    // The child's action that needs to be shown in the page's title bar as main action.
    // This is a way for the page to show a UI component created and controlled by a child components
    const [pageMainAction, setPageMainAction] = useState<JSX.Element | undefined>();


    /**
     * This useEffect is used for loading Robot data on component init
     */
    useEffect(() => {
        if (robotId && homeId && !robot) {
            rcDataDispatch(fetchRobotById(projectId, homeId, robotId)).catch(() => { /* ignore error */ });
        }
    }, [projectId, robot, robotId, homeId, rcDataDispatch]);


    /**
     * This useEffect is used for loading robot definition if it is not loaded
     */
    useEffect(() => {
        if (robot && !robotDefinition) {
            rcDataDispatch(fetchRobotCatalog(projectId)).catch(() => {
                // ignore error because dataDispatch already handled the error
            });
        }
    }, [projectId, rcDataDispatch, robot, robotDefinition]);



    return (
        <div className={clsx('page-main-content')}>
            <div className="title-bar">
                <div className="title-bar-item title-text">
                    <div className="page-title">
                        {trans('page.title.robot_data.historical')}
                    </div>
                    <div className="page-subtitle">
                        {capitalizeAll(trans('page.subtitle.robot_data.historical'))}
                    </div>
                </div>
                {pageMainAction}
            </div>

            {project && robot && (
                <>
                    {robot.deviceId && robotCollectionId ? (
                        <CollectionMetricsDataProvider
                            projectId={projectId}
                            homeId={robot.homeId}
                            deviceId={robot.deviceId}
                            collectionId={robotCollectionId}
                            collectionDisplayName={robot.name || robot.id}
                            numericMetrics={robotDefinition?.numericMetrics}
                            tagMetrics={robotDefinition?.tagMetrics}
                            setPageMainAction={setPageMainAction}
                            viewOptions={chartViewOptions}
                            viewTypeSettings={dataViewTypeSettings}
                        />
                    ) : (
                        <Box marginTop={3}>
                            <GenericMessageBox
                                title={capitalizeFirst(trans('error.robot_is_not_connected.message'))}
                            />
                        </Box>
                    )}
                </>
            )}

            {(!project || !robot) && (
                <CircularProgress />
            )}
        </div>
    );
};
